import React, { useState, useEffect } from "react";
import Drawer from "../common/Drawer";
import styles from "./ProductDetailsDrawer.module.css";
import {
	fetchProductDetails,
	type ProductAttribute,
} from "../../services/productDetailsService";
import { useViewModel } from "../../hooks/useViewModel.ts";

interface Product {
	materialNumber?: string;
	kind: string;
	title: string;
	meta: string[];
	description: string;
	imageUrl: string;
	stockStatus?: {
		iconClass?: string;
		description?: string;
	};
	priceDifference?: {
		net: number;
		gross: number;
	};
	manufacturer?: {
		imageUrl: string;
		name: string;
	};
}

interface ProductDetailsDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	product: Product;
	isSelected?: boolean;
	onProductSelect?: (materialNumber: string) => void;
}

interface AttributeGroup {
	name: string;
	attributes: ProductAttribute[];
}

const ProductDetailsDrawer: React.FC<ProductDetailsDrawerProps> = ({
	isOpen,
	onClose,
	product,
	isSelected = false,
	onProductSelect,
}) => {
	const [attributes, setAttributes] = useState<ProductAttribute[]>([]);
	const [productInfo, setProductInfo] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showFullDescription, setShowFullDescription] = useState(false);
	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
	const { data } = useViewModel();
	const { strings } = data!;

	const maxDisplayedAttributes = 3;
	const maxDescriptionLength = 200;

	useEffect(() => {
		if (!isOpen || !product.materialNumber) return;

		const loadProductDetails = async () => {
			setIsLoading(true);
			setError(null);
			try {
				const data = await fetchProductDetails(product.materialNumber!);
				setAttributes(
					data.displayAttributes.length > 0 ? data.displayAttributes : [],
				);
				setProductInfo(data.productInfo);
			} catch (err) {
				setError(strings.product.configBundles.cannotLoadProductDetails);
				console.error(err);
			} finally {
				setIsLoading(false);
			}
		};

		loadProductDetails();
	}, [isOpen, product.materialNumber]);

	const formatPrice = (price: number): string => {
		return new Intl.NumberFormat("no-NO", {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(Math.abs(price));
	};

	const getPriceSign = (price: number): string => {
		if (price > 0) return "+";
		if (price < 0) return "-";
		return "";
	};

	const formattedAttributes: AttributeGroup[] = attributes.reduce(
		(acc: AttributeGroup[], attr) => {
			const existingGroup = acc.find((g) => g.name === attr.group);
			if (existingGroup) {
				existingGroup.attributes.push(attr);
			} else {
				acc.push({
					name: attr.group,
					attributes: [attr],
				});
			}
			return acc;
		},
		[],
	);

	const handleSelectProduct = () => {
		if (onProductSelect && product.materialNumber) {
			onProductSelect(product.materialNumber);
		}
	};

	const toggleGroup = (groupName: string) => {
		setExpandedGroups((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(groupName)) {
				newSet.delete(groupName);
			} else {
				newSet.add(groupName);
			}
			return newSet;
		});
	};

	const buttonText = isSelected
		? strings.product.configBundles.selected
		: strings.product.configBundles.select;
	const priceGross = product.priceDifference?.gross ?? 0;

	return (
		<Drawer isOpen={isOpen} onClose={onClose} title={product.title}>
			<div className={styles.drawerContent}>
				{onProductSelect && (
					<div className={styles.priceSection}>
						<div className={styles.priceInfo}>
							<div className={styles.mainPrice}>
								{getPriceSign(priceGross)} {formatPrice(priceGross)},-
							</div>
						</div>
						<button
							className={styles.selectButton}
							onClick={handleSelectProduct}
							disabled={isSelected}
							type="button"
						>
							{buttonText}
						</button>
					</div>
				)}

				<div className={styles.imageSection}>
					<div className={styles.imageContainer}>
						<img
							src={`/img/p/800/${product.materialNumber}.jpg`}
							srcSet={`/img/p/400/${product.materialNumber}.jpg 400w, /img/p/600/${product.materialNumber}.jpg 600w, /img/p/800/${product.materialNumber}.jpg 800w /img/p/1200/${product.materialNumber}.jpg 1200w`}
							sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 800px"
							height={800}
							alt={product.title}
							className={styles.productImage}
						/>
					</div>
				</div>

				<div className={styles.imageSection}>
					{product.manufacturer && (
						<div className={styles.manufacturerLogo}>
							<img
								src={product.manufacturer.imageUrl}
								alt={product.manufacturer.name}
							/>
						</div>
					)}
				</div>

				{/* Beskrivelse (Description) */}
				<div className={styles.section}>
					<h2 className={styles.mainSectionTitle}>
						{strings.product.configBundles.description}
					</h2>
					{product.materialNumber && (
						<p className={styles.productNumber}>
							Varenummer: {product.materialNumber}
						</p>
					)}
					<div className={styles.descriptionText}>
						{showFullDescription ||
						product.description?.length < maxDescriptionLength
							? product.description
							: product.description?.substring(0, maxDescriptionLength) + "..."}
					</div>
					{product.description?.length > maxDescriptionLength && (
						<button
							className={styles.readMoreButton}
							onClick={() => setShowFullDescription(!showFullDescription)}
							aria-expanded={showFullDescription}
						>
							{showFullDescription
								? "Vis mindre"
								: strings.product.configBundles.readMore}
						</button>
					)}
				</div>

				{productInfo && (
					<div className={styles.section}>
						<h2 className={styles.mainSectionTitle}>
							{strings.product.configBundles.productInfo}
						</h2>
						<div
							className={styles.productInfoContent}
							dangerouslySetInnerHTML={{ __html: productInfo }}
						/>
					</div>
				)}

				{/* Error State */}
				{error && (
					<div className={styles.error} role="alert" aria-live="assertive">
						<p>{error}</p>
					</div>
				)}

				{/* Loading State */}
				{isLoading && (
					<div className={styles.loading}>
						<p>{strings.product.configBundles.loadingProductDetails}</p>
					</div>
				)}

				{/* Spesifikasjoner (Specifications) */}
				{formattedAttributes.length > 0 && (
					<div className={styles.section}>
						<h2 className={styles.mainSectionTitle}>
							{strings.product.configBundles.specifications}
						</h2>
						{formattedAttributes.map((group) => (
							<div key={group.name} className={styles.specGroup}>
								<h3 className={styles.specGroupTitle}>{group.name}</h3>
								<div className={styles.specTable}>
									{(expandedGroups.has(group.name)
										? group.attributes
										: group.attributes.slice(0, maxDisplayedAttributes)
									).map((attr, index) => (
										<div key={index} className={styles.specRow}>
											<div className={styles.specLabel}>{attr.key}</div>
											<div
												className={styles.specValue}
												dangerouslySetInnerHTML={{ __html: attr.value }}
											/>
										</div>
									))}
								</div>
								{group.attributes.length > 3 && (
									<button
										className={styles.readMoreButton}
										onClick={() => toggleGroup(group.name)}
										aria-expanded={expandedGroups.has(group.name)}
									>
										{expandedGroups.has(group.name)
											? "Vis mindre"
											: strings.product.configBundles.readMore}
									</button>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</Drawer>
	);
};

export default ProductDetailsDrawer;